import { useRef, useState } from 'react';
import { Button } from '@jywer/ui';
import {
  attachContractToOrder,
  downloadContractUrl,
  fetchAdminOrder,
  initContractUpload,
  uploadContract,
} from './api';
import type { AdminOrderDetail } from './types';

interface ContractPanelProps {
  order: AdminOrderDetail;
  onUpdated: (updated: AdminOrderDetail) => void;
  disabled?: boolean;
}

export function ContractPanel({ order, onUpdated, disabled = false }: ContractPanelProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onPickFile = (replace: boolean) => {
    setErr(null);
    if (fileRef.current) {
      fileRef.current.value = '';
      // The "replace" flag is handled via the same code path — both
      // overwrite the R2 key on the order.
      void replace;
      fileRef.current.click();
    }
  };

  const onFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr(null);
    setUploading(true);
    try {
      const init = await initContractUpload(file.name, file.type);
      await uploadContract(init.upload_url, file);
      await attachContractToOrder(order.id, init.r2_key);
      const updated = await fetchAdminOrder(order.id);
      onUpdated(updated);
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'Upload fejlede');
    } finally {
      setUploading(false);
    }
  };

  const downloadHref = downloadContractUrl(order.id);

  return (
    <div className="flex flex-col gap-3 rounded border border-line-1 bg-paper-0 p-4">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-2">Kontrakt</h2>

      {order.signed_contract_present ? (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-ink-0">
            <strong>{order.signed_contract_filename ?? 'Kontrakt'}</strong>
          </p>
          {order.signed_contract_uploaded_at ? (
            <p className="text-xs text-ink-2">
              Uploadet {new Date(order.signed_contract_uploaded_at).toLocaleString('da-DK')}
            </p>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <a
              href={downloadHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 rounded border border-line-2 px-3 py-1.5 text-sm font-medium text-primary hover:bg-paper-1"
            >
              Download ↗
            </a>
            <Button
              variant="secondary"
              onClick={() => onPickFile(true)}
              disabled={disabled || uploading}
            >
              {uploading ? 'Erstatter…' : 'Erstat'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-ink-2">Ingen kontrakt vedhæftet.</p>
          <div>
            <Button onClick={() => onPickFile(false)} disabled={disabled || uploading}>
              {uploading ? 'Uploader…' : 'Upload kontrakt'}
            </Button>
          </div>
        </div>
      )}

      {err ? (
        <p className="rounded border border-status-err/40 bg-status-err/5 px-3 py-2 text-xs text-status-err">
          {err}
        </p>
      ) : null}

      <input
        ref={fileRef}
        type="file"
        accept="application/pdf,image/png,image/jpeg"
        onChange={onFileChosen}
        className="hidden"
      />
    </div>
  );
}
